import { systemClock } from './clock';

/**
 * A UUIDv7, minted in the browser.
 *
 * **Not `crypto.randomUUID()`, which is a v4.** Constitution Principle V asks
 * for client-generatable keys that are UUIDv7 or ULID, and `VoidTicketDto` says
 * UUIDv7 in as many words. The server's `@IsUUID()` would accept a v4 today,
 * which is exactly why this is worth stating: the version is not enforced at the
 * boundary, so nothing would fail until somebody looked at an index that had
 * stopped being time-ordered. A v7's leading 48 bits are the timestamp, which is
 * what keeps rows written from a hundred terminals and a back office clustered
 * in the order they happened rather than scattered across the B-tree.
 *
 * **There is a second copy of this in `apps/desktop/src/main/ticketing/uuidv7.ts`
 * and it is not an oversight.** That one runs in the Electron main process and
 * takes its bytes from `node:crypto`; a `node:` import in this bundle does not
 * survive the build. The bit-laying is RFC 9562 §5.7 and is fixed by the
 * specification rather than by a decision either file could make differently, so
 * the usual argument against two copies — that they drift — has nothing to act
 * on here. A shared package would still be the better home; noted in the PR
 * rather than done, because moving the terminal's copy is another ticket's file.
 *
 * **No monotonic counter within a millisecond.** RFC 9562 makes it optional and
 * the 74 random bits carry the ordering burden instead; two ids minted in the
 * same millisecond may sort either way between themselves, which decides
 * nothing — the server's own `recorded_at` orders things, and this is a key
 * rather than a clock.
 */

/** Milliseconds since the Unix epoch. Injected, never read from in here. */
export type EpochMilliseconds = () => number;

export function uuidv7(now: EpochMilliseconds): string {
  const timestamp = Math.max(0, Math.trunc(now()));

  const bytes = crypto.getRandomValues(new Uint8Array(16));

  // 48 bits of big-endian Unix milliseconds across bytes 0-5. Written a byte at
  // a time because the value exceeds what a 32-bit shift can carry — `<<` in
  // JavaScript coerces to int32, so the obvious version of this silently
  // truncates every timestamp after 1970-01-25.
  bytes[0] = Math.floor(timestamp / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(timestamp / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(timestamp / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(timestamp / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(timestamp / 2 ** 8) & 0xff;
  bytes[5] = timestamp & 0xff;

  // Version 7 in the high nibble of byte 6, keeping the 12 random bits below it.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;

  // The RFC 4122 variant in the top two bits of byte 8, keeping the 62 random
  // bits below it.
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * A fresh id for one write, bound to the real clock.
 *
 * **Bound here rather than injected through the tree**, which is the same
 * departure `lib/idempotency-key.ts` argues for and the same reasoning: a clock
 * is threaded through this app wherever behaviour depends on the *value* it
 * returns, because an expiry either has passed or has not and a test must pin
 * which. Nothing depends on this value. The id is opaque to the app, and what a
 * test asserts is that two requests carried the *same* one — visible without
 * knowing what it was. The seam that a test would actually want is
 * `uuidv7`'s own parameter, which is right there.
 *
 * `crypto.getRandomValues` needs a secure context, as `crypto.randomUUID` does.
 * The back office is served over HTTPS and the desktop shell registers `app://`
 * with `secure: true`, so both targets have it.
 */
export const newUuidv7 = (): string => uuidv7(systemClock);
